<template>
  <div class="chart-page">
    <div class="chart-toolbar">
      <div class="toolbar-row">
        <pods-input ref="podsInput" />
        <div class="toolbar-actions">
          <el-tooltip placement="left">
            <div slot="content">
              {{
                connectionStatus === 0
                  ? 'Connecting to Smartz® service...'
                  : connectionStatus === 1
                  ? 'Successfully connected to Smartz® service.'
                  : 'Failed to connect to Smartz® service.'
              }}
            </div>
            <span
              class="connection-badge"
              :class="
                connectionStatus === 1
                  ? 'green-bg'
                  : connectionStatus === 2
                  ? 'red-bg'
                  : ''
              "
            >
              {{
                connectionStatus === 0
                  ? 'Connecting...'
                  : connectionStatus === 1
                  ? 'Connected'
                  : 'Disconnected'
              }}
            </span>
          </el-tooltip>
        </div>
      </div>
      <div class="toolbar-row">
        <el-select
          v-model="selectedColumns"
          multiple
          collapse-tags
          placeholder="Columns"
          size="small"
          style="width: 220px"
        >
          <el-option
            v-for="column in availableColumns"
            :key="column"
            :label="getColumnLabel(column)"
            :value="column"
          />
        </el-select>
        <div class="toolbar-actions">
          <UtcOffsets />
        </div>
      </div>
    </div>

    <div class="app-card" style="overflow-x: auto; padding: 0">
      <el-table :data="tableData" border style="width: 100%" size="small">
        <el-table-column
          v-for="column in selectedColumns"
          :key="column"
          :prop="column"
          :label="getColumnLabel(column)"
        />
      </el-table>
    </div>
  </div>
</template>

<script>
import PodsInput from '@/components/PodsInput'
import UtcOffsets from '@/components/UtcOffsets'

// Column label mappings for better display
const COLUMN_LABELS = {
  tickCounter: 'Tickcounter',
  connectionStatus: 'Connection Status',
  sensor1Value: 'Sensor 1 / Aggregate',
  sensor2Value: 'Sensor 2 / Refined',
  ax: 'Ax',
  ay: 'Ay',
  az: 'Az',
  lastConnectedTickCounter: 'Last Pad Connection',
  batteryVoltage: 'Battery Voltage',
  temperature: 'Temperature',
  fallEvent: 'Fall Event',
  lastFallTickCounter: 'Fall TickCounter',
  firmwareVersion: 'FW Version',
  timeStampWithUtc: 'Time',
  podMac: 'Pod MAC',
  podId: 'Pod ID',
  podStatus: 'Pod Status'
}

// Default columns to display
const DEFAULT_COLUMNS = [
  'tickCounter',
  'connectionStatus',
  'sensor1Value',
  'sensor2Value',
  'ax',
  'ay',
  'az',
  'lastConnectedTickCounter',
  'batteryVoltage',
  'temperature',
  'fallEvent',
  'lastFallTickCounter',
  'firmwareVersion',
  'timeStampWithUtc'
]

export default {
  props: {
    podData: { require: true, type: Array },
    screenWidth: { require: true, type: Number },
    connectionStatus: { type: Number, default: 2 }
  },
  data() {
    return {
      selectedColumns: [...DEFAULT_COLUMNS],
      availableColumns: [],
      columnLabels: COLUMN_LABELS
    }
  },
  computed: {
    tableData() {
      let data = JSON.parse(JSON.stringify(this.podData))
      return data.reverse()
    }
  },
  watch: {
    // Watch podData to update available columns dynamically
    podData: {
      deep: true,
      handler() {
        this.updateAvailableColumns()
      }
    }
  },
  mounted() {
    // Initialize available columns from current podData
    this.updateAvailableColumns()
  },
  components: {
    PodsInput,
    UtcOffsets
  },
  methods: {
    /**
     * Update available columns based on data received from SignalR
     */
    updateAvailableColumns() {
      if (!this.podData || this.podData.length === 0) {
        this.availableColumns = [...DEFAULT_COLUMNS]
        return
      }

      // Get all keys from the first data object
      const firstDataKeys = Object.keys(this.podData[0])

      // Filter out timestamp-related columns
      const excludedColumns = ['timeStamp', 'timestamp']
      const filteredKeys = firstDataKeys.filter(
        key => !excludedColumns.includes(key.toLowerCase())
      )

      // Merge with default columns and remove duplicates
      const uniqueColumns = new Set([...DEFAULT_COLUMNS, ...filteredKeys])
      this.availableColumns = Array.from(uniqueColumns)

      // Ensure currently selected columns are valid
      this.selectedColumns = this.selectedColumns.filter(col =>
        this.availableColumns.includes(col)
      )

      // If no columns are selected, use defaults
      if (this.selectedColumns.length === 0) {
        this.selectedColumns = DEFAULT_COLUMNS.filter(col =>
          this.availableColumns.includes(col)
        )
      }
    },

    /**
     * Get display label for a column
     */
    getColumnLabel(column) {
      return COLUMN_LABELS[column] || column
    }
  }
}
</script>

<style lang="scss" scoped>
.connection-badge {
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: #bbb;
  color: white;
  border-radius: 4px;
  white-space: nowrap;

  &.red-bg {
    background-color: #ff4d4d;
  }
  &.green-bg {
    background-color: #4caf50;
  }
}
</style>
